import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Calendar, Clock, User, MapPin, 
  CreditCard, Lock, CheckCircle, Loader2, AlertCircle
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Elements, 
  PaymentElement, 
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js';
import { format } from 'date-fns';
import { useBooking } from '../context/BookingContext';
import { useAuth } from '../context/AuthContext';
import { createBooking } from '../services/firestore';

// Initialize Stripe - Replace with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_...');

const CheckoutForm: React.FC<{ 
  onSuccess: () => void;
  amount: number;
}> = ({ onSuccess, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Payment failed');
        setLoading(false);
        return;
      }

      // In a real implementation, you would create a payment intent on your server
      // and confirm the payment here
      
      // Simulating successful payment for demo
      await new Promise(resolve => setTimeout(resolve, 2000));
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      
      {error && (
        <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle size={18} className="mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Lock size={18} className="mr-2" />
            Pay £{amount.toFixed(2)}
          </>
        )}
      </button>

      <p className="text-center text-xs text-gray-500 flex items-center justify-center">
        <Lock size={12} className="mr-1" />
        Secured by Stripe. Your payment information is encrypted.
      </p>
    </form>
  );
};

const Checkout: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bookingState, getTotalPrice, getTotalDuration, resetBooking } = useBooking();
  
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);

  useEffect(() => {
    // Redirect if no booking data
    if (!bookingState.selectedDate || !bookingState.selectedTime || bookingState.selectedServices.length === 0) {
      navigate(`/shop/${businessId}`);
    }
  }, [bookingState, businessId, navigate]);

  const handlePaymentSuccess = async () => {
    try {
      // Create booking in Firestore
      const booking = await createBooking({
        BusinessUserAccountId: businessId || '',
        customerId: user?.uid || 'guest',
        customerName: user?.displayName || bookingState.customerInfo.name,
        customerEmail: user?.email || bookingState.customerInfo.email,
        customerPhone: bookingState.customerInfo.phone,
        services: bookingState.selectedServices,
        staffId: bookingState.selectedStaff?.id,
        date: bookingState.selectedDate!.toISOString(),
        time: bookingState.selectedTime!,
        totalPrice: getTotalPrice(),
        totalDuration: getTotalDuration(),
        status: 'confirmed',
        paymentStatus: 'paid',
        createdAt: new Date().toISOString(),
      });

      setBookingId(booking);
      setPaymentSuccess(true);
    } catch (error) {
      console.error('Error creating booking:', error);
    }
  };

  const handleDone = () => {
    resetBooking();
    navigate('/bookings');
  };

  // Success Screen
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-prim-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle size={40} className="text-green-500" />
            </motion.div>

            <h1 className="text-2xl font-display font-bold text-prim-text mb-2">
              Booking Confirmed!
            </h1>
            <p className="text-gray-500 mb-6">
              Your appointment has been successfully booked and paid for.
            </p>

            {/* Booking Details */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <h3 className="font-semibold text-prim-text mb-3">
                {bookingState.business?.name}
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2 text-primary" />
                  {bookingState.selectedDate && format(bookingState.selectedDate, 'EEEE, MMMM d, yyyy')}
                </div>
                <div className="flex items-center">
                  <Clock size={16} className="mr-2 text-primary" />
                  {bookingState.selectedTime}
                </div>
                {bookingState.selectedStaff && (
                  <div className="flex items-center">
                    <User size={16} className="mr-2 text-primary" />
                    {bookingState.selectedStaff.name}
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="text-gray-600">Total Paid</span>
                <span className="text-xl font-bold text-prim-text">
                  £{getTotalPrice().toFixed(2)}
                </span>
              </div>
            </div>

            {bookingId && (
              <p className="text-xs text-gray-400 mb-6">
                Booking Reference: #{bookingId.slice(0, 8).toUpperCase()}
              </p>
            )}

            <button
              onClick={handleDone}
              className="w-full py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors"
            >
              View My Bookings
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-prim-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 hover:bg-gray-50 rounded-full"
            >
              <ArrowLeft size={20} className="text-prim-text" />
            </button>
            <h1 className="ml-4 text-lg font-semibold text-prim-text">
              Checkout
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Order Summary */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden sticky top-24">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-prim-text">Order Summary</h3>
              </div>

              {/* Business */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center">
                  {bookingState.business?.businessImage ? (
                    <img
                      src={bookingState.business.businessImage}
                      alt={bookingState.business.name}
                      className="w-12 h-12 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-primary-light flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">
                        {bookingState.business?.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="ml-3">
                    <p className="font-medium text-prim-text">{bookingState.business?.name}</p>
                    <p className="text-xs text-gray-500 flex items-center">
                      <MapPin size={12} className="mr-1" />
                      {bookingState.business?.postCode}
                    </p>
                  </div>
                </div>
              </div>

              {/* Appointment Details */}
              <div className="p-4 border-b border-gray-100 space-y-2 text-sm">
                <div className="flex items-center text-gray-600">
                  <Calendar size={16} className="mr-2 text-primary" />
                  {bookingState.selectedDate && format(bookingState.selectedDate, 'EEE, MMM d, yyyy')}
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock size={16} className="mr-2 text-primary" />
                  {bookingState.selectedTime} • {getTotalDuration()} mins
                </div>
                {bookingState.selectedStaff && (
                  <div className="flex items-center text-gray-600">
                    <User size={16} className="mr-2 text-primary" />
                    {bookingState.selectedStaff.name}
                  </div>
                )}
              </div>

              {/* Services */}
              <div className="p-4 border-b border-gray-100">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Services</h4>
                <div className="space-y-2">
                  {bookingState.selectedServices.map(({ service }) => (
                    <div key={service.id} className="flex justify-between text-sm">
                      <span className="text-gray-600">{service.name}</span>
                      <span className="text-prim-text font-medium">
                        £{service.price.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="p-4 bg-gray-50">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-prim-text">Total</span>
                  <span className="text-2xl font-bold text-prim-text">
                    £{getTotalPrice().toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center mb-6">
                <CreditCard size={24} className="text-primary mr-3" />
                <h2 className="text-lg font-semibold text-prim-text">
                  Payment Details
                </h2>
              </div>

              <Elements 
                stripe={stripePromise}
                options={{
                  mode: 'payment',
                  amount: Math.round(getTotalPrice() * 100),
                  currency: 'gbp',
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#1389FD',
                      borderRadius: '12px',
                    },
                  },
                }}
              >
                <CheckoutForm 
                  onSuccess={handlePaymentSuccess} 
                  amount={getTotalPrice()} 
                />
              </Elements>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;

